import * as React from "react"

// The decoy field's name, which must match PublicFormGuard::DECOY_FIELD on the
// server. It travels at the top level of the posted params, outside the
// record's own hash, so it can never be mistaken for one of its attributes.
export const HONEYPOT_FIELD = "company"

// A field nobody filling the real form can see, tab to, or hear: off-screen,
// out of the tab order, hidden from assistive technology and excluded from
// autofill. A bot that fills every input it finds fills this one too, and the
// server then answers as if the submission succeeded while sending and storing
// nothing.
//
// The id is passed in because a page can carry more than one of these (the
// Book page has the booking form beside the callback form) and duplicate ids
// would be invalid markup.
function HoneypotField({
  id,
  value,
  onChange,
}: {
  id: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div aria-hidden="true" className="absolute left-[-9999px] h-px w-px overflow-hidden">
      <label htmlFor={id}>Company</label>
      <input
        id={id}
        name={HONEYPOT_FIELD}
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

// Wires one form's decoy: render `field` inside the form and spread `payload`
// into the posted params alongside the record's own hash.
export function useHoneypot(id: string) {
  const [value, setValue] = React.useState("")

  return {
    field: <HoneypotField id={id} value={value} onChange={setValue} />,
    payload: { [HONEYPOT_FIELD]: value },
  }
}

// A whole-form error, as opposed to one attached to a field — currently the
// rate limiter's refusal, which belongs to no single input. Without this the
// server's `errors.base` would arrive and never be shown.
export function FormError({ message }: { message?: string }) {
  if (!message) return null

  return (
    <p role="alert" className="rounded-lg bg-danger-faded px-4 py-3 text-sm text-danger-display">
      {message}
    </p>
  )
}
